import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessages, useIntl } from 'react-intl';

import type { Currency } from '#src/__generated__/globalTypes.ts';
import { AccountEntry } from '#src/components/mySorare/TransactionsHistory/UserAccountEntries/AccountEntry/index.tsx';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { isType } from '#src/lib/gql/index.ts';

import type { InternalTransfer_userAccountEntry } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Internal Transfer',
  },
  sorareCredit: {
    defaultMessage: 'Sorare fund Credit',
  },
  sorareOperation: {
    defaultMessage: 'Sorare operation',
  },
  generic: {
    defaultMessage: 'Internal transfer',
  },
});

type Props = {
  userAccountEntry: InternalTransfer_userAccountEntry;
  primaryCurrency: Currency;
};

export const InternalTransfer = ({
  userAccountEntry,
  primaryCurrency,
}: Props) => {
  const { tokenOperation, amounts } = userAccountEntry;
  const { formatMessage } = useIntl();
  const { toMonetaryAmount } = useMonetaryAmount();

  if (!isType(tokenOperation, 'MangopayInternalTransfer')) {
    return null;
  }

  let detail: string;
  if (tokenOperation.withSorare) {
    detail = formatMessage(
      toMonetaryAmount(amounts).isPositive
        ? messages.sorareCredit
        : messages.sorareOperation
    );
  } else {
    detail = formatMessage(messages.generic);
  }

  return (
    <AccountEntry
      userAccountEntry={userAccountEntry}
      title={formatMessage(messages.title)}
      primaryCurrency={primaryCurrency}
    >
      {detail}
    </AccountEntry>
  );
};

InternalTransfer.fragments = {
  userAccountEntry: gql`
    fragment InternalTransfer_userAccountEntry on UserAccountEntry {
      id
      tokenOperation {
        ... on MangopayInternalTransfer {
          id
          withSorare
        }
      }
      ...AccountEntry_userAccountEntry
    }
    ${AccountEntry.fragments.userAccountEntry}
  ` as TypedDocumentNode<InternalTransfer_userAccountEntry>,
};
